import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { addTrainingComment, deleteTrainingComment, listTrainingComments } from '../services/learningCenterService';
import type { TrainingComment, TrainingCommentPath } from '../types';

const TrainingComments: React.FC<{ path: TrainingCommentPath; prompt?: string; onCount?: (count: number) => void }> = ({ path, prompt, onCount }) => {
  const { currentUser, userData } = useAuth();
  const [comments, setComments] = useState<TrainingComment[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = () => listTrainingComments(path)
    .then(items => {
      setComments(items);
      onCount?.(items.length);
    })
    .catch(() => setMessage('Could not load comments.'));

  useEffect(() => {
    setMessage('');
    void load();
  }, [path.collection, path.id]);

  const submit = async () => {
    if (!currentUser?.uid || !draft.trim() || busy) return;
    setBusy(true); setMessage('');
    try {
      await addTrainingComment(path, {
        authorId: currentUser.uid,
        authorName: String(userData?.displayName || currentUser.displayName || 'Overture teacher'),
        text: draft,
      });
      setDraft('');
      await load();
    } catch (error: any) {
      setMessage(error?.message || 'Could not post comment.');
    } finally { setBusy(false); }
  };

  const remove = async (commentId: string) => {
    setBusy(true); setMessage('');
    try {
      await deleteTrainingComment(path, commentId);
      await load();
    } catch (error: any) {
      setMessage(error?.message || 'Could not delete comment.');
    } finally { setBusy(false); }
  };

  return (
    <div className="lesson-feed__comments">
      <h3>{prompt || 'Comments'}</h3>
      <div className="lesson-feed__comment-list">
        {comments.map(comment => (
          <article key={comment.id}>
            <div>
              <strong>{comment.authorName}</strong>
              <p>{comment.text}</p>
            </div>
            {currentUser?.uid === comment.authorId && (
              <button type="button" onClick={() => void remove(comment.id)} disabled={busy}>Delete</button>
            )}
          </article>
        ))}
        {!comments.length && <p className="lesson-feed__comment-empty">No replies yet. Jump in.</p>}
      </div>
      {message && <p className="lesson-feed__comment-empty">{message}</p>}
      <form className="lesson-feed__comment-form" onSubmit={event => { event.preventDefault(); void submit(); }}>
        <input
          value={draft}
          maxLength={500}
          placeholder={prompt || 'Add a comment…'}
          onChange={event => setDraft(event.target.value)}
        />
        <button type="submit" disabled={busy || !draft.trim()}>{busy ? '…' : 'Post'}</button>
      </form>
    </div>
  );
};

export default TrainingComments;
